import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import alumniService from '../../services/alumniService';
import DialogModal from '../../components/DialogModal';
import { MagnifyingGlass, Plus, PencilSimple, Trash, PaperPlaneTilt, HandCoins, Check, X as XIcon } from '@phosphor-icons/react';

const emptyForm = {
  first_name: '',
  last_name: '',
  other_names: '',
  graduation_year: '',
  last_class_completed: '',
  current_occupation: '',
  current_institution: '',
  phone: '',
  email: '',
  address: '',
  opt_in_communications: true,
  notes: '',
};

function AlumniRecordsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [outreachMessage, setOutreachMessage] = useState('');
  const [outreachYear, setOutreachYear] = useState('');
  const [sendingOutreach, setSendingOutreach] = useState(false);
  const [donationsSummary, setDonationsSummary] = useState(null);
  const [donations, setDonations] = useState([]);
  const [donationAlumniId, setDonationAlumniId] = useState('');
  const [donationAmount, setDonationAmount] = useState('');
  const [donationDate, setDonationDate] = useState(new Date().toISOString().split('T')[0]);
  const [donationCampaign, setDonationCampaign] = useState('');
  const [loggingDonation, setLoggingDonation] = useState(false);
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, defaultValue, onSubmit }

  useEffect(() => {
    loadData();
    loadDonationsSummary();
    loadDonations();
  }, []);

  const loadDonations = async () => {
    try {
      const data = await alumniService.listDonations();
      setDonations(Array.isArray(data) ? data : []);
    } catch (error) {
      // non-fatal — summary line still renders without the list
    }
  };

  const getAlumniName = (alumniId) => {
    const alumnus = records.find((r) => r.id === alumniId);
    return alumnus ? `${alumnus.first_name} ${alumnus.last_name}` : alumniId;
  };

  const handleApproveDonation = async (donation) => {
    try {
      await alumniService.approveDonation(donation.id);
      toast.success('Donation approved');
      loadDonationsSummary();
      loadDonations();
    } catch (error) {
      toast.error(error.message || 'Failed to approve donation');
    }
  };

  const handleRejectDonation = (donation) => {
    setPromptDialog({
      title: 'Reject Donation',
      message: 'Reason for rejecting this donation (optional):',
      defaultValue: '',
      onSubmit: async (value) => {
        const reason = value?.trim() || undefined;
        try {
          await alumniService.rejectDonation(donation.id, reason);
          toast.success('Donation rejected');
          loadDonations();
        } catch (error) {
          toast.error(error.message || 'Failed to reject donation');
        }
      },
    });
  };

  useEffect(() => {
    let filtered = records;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((r) => `${r.first_name} ${r.last_name}`.toLowerCase().includes(term));
    }
    if (yearFilter !== 'all') {
      filtered = filtered.filter((r) => r.graduation_year === yearFilter);
    }
    setFilteredRecords(filtered);
  }, [records, searchTerm, yearFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await alumniService.listRecords();
      setRecords(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load alumni records');
    } finally {
      setLoading(false);
    }
  };

  const loadDonationsSummary = async () => {
    try {
      const summary = await alumniService.getDonationsSummary();
      setDonationsSummary(summary);
    } catch (error) {
      // non-critical — the page still works without the summary
    }
  };

  const graduationYears = [...new Set(records.map((r) => r.graduation_year))].sort().reverse();

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (record) => {
    setEditingId(record.id);
    setFormData({
      first_name: record.first_name,
      last_name: record.last_name,
      other_names: record.other_names || '',
      graduation_year: record.graduation_year,
      last_class_completed: record.last_class_completed || '',
      current_occupation: record.current_occupation || '',
      current_institution: record.current_institution || '',
      phone: record.phone || '',
      email: record.email || '',
      address: record.address || '',
      opt_in_communications: record.opt_in_communications,
      notes: record.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.graduation_year) {
      toast.error('First name, last name, and graduation year are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await alumniService.updateRecord(editingId, formData);
        toast.success('Alumni record updated');
      } else {
        await alumniService.createRecord(formData);
        toast.success('Alumni record added');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save alumni record');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await alumniService.deleteRecord(deleteTarget);
      toast.success('Alumni record deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete alumni record');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSendOutreach = async () => {
    if (!outreachMessage.trim()) {
      toast.error('Enter a message to send');
      return;
    }
    setSendingOutreach(true);
    try {
      const result = await alumniService.sendOutreach({
        message: outreachMessage,
        graduation_year: outreachYear || undefined,
      });
      toast.success(
        `Outreach sent to ${result.recipients_matched} opted-in alumni (${result.sms_sent_to} SMS, ${result.emails_sent_to} email)`
      );
      setOutreachMessage('');
    } catch (error) {
      toast.error(error.message || 'Failed to send outreach');
    } finally {
      setSendingOutreach(false);
    }
  };

  const handleLogDonation = async () => {
    if (!donationAlumniId || !donationAmount) {
      toast.error('Select an alumnus and enter an amount');
      return;
    }
    setLoggingDonation(true);
    try {
      await alumniService.createDonation(donationAlumniId, {
        amount: Number(donationAmount),
        donation_date: donationDate,
        campaign: donationCampaign || null,
      });
      toast.success('Donation logged');
      setDonationAmount('');
      setDonationCampaign('');
      loadDonationsSummary();
      loadDonations();
    } catch (error) {
      toast.error(error.message || 'Failed to log donation');
    } finally {
      setLoggingDonation(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading alumni records...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Alumni Records</h1>
            <p>Track former students and reach out to those opted into communications</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            Add Alumni Record
          </button>
        </div>

        <div className="form-card" style={{ marginBottom: 20, padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Send Outreach</h3>
          <p style={{ color: '#666', fontSize: 13, marginTop: -8 }}>
            Sends to all opted-in alumni (optionally filtered by graduation year) via SMS/email.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 240 }}>
              <label>Message</label>
              <textarea value={outreachMessage} onChange={(e) => setOutreachMessage(e.target.value)} placeholder="e.g., Join us for our 10th anniversary reunion!" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Graduation Year (optional)</label>
              <select value={outreachYear} onChange={(e) => setOutreachYear(e.target.value)} className="filter-select">
                <option value="">All Years</option>
                {graduationYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button className="btn btn-primary" onClick={handleSendOutreach} disabled={sendingOutreach}>
              <PaperPlaneTilt size={18} />
              {sendingOutreach ? 'Sending...' : 'Send Outreach'}
            </button>
          </div>
        </div>

        <div className="form-card" style={{ marginBottom: 20, padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Donations & Fundraising</h3>
          {donationsSummary && (
            <p style={{ color: '#444', fontSize: 14, marginTop: -8 }}>
              Total raised: <strong>GHS {Number(donationsSummary.total_raised).toFixed(2)}</strong>
              {Object.keys(donationsSummary.by_campaign || {}).length > 0 && (
                <span>
                  {' '}— {Object.entries(donationsSummary.by_campaign).map(([campaign, amount]) => (
                    `${campaign}: GHS ${Number(amount).toFixed(2)}`
                  )).join(', ')}
                </span>
              )}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ margin: 0, minWidth: 200 }}>
              <label>Alumnus</label>
              <select value={donationAlumniId} onChange={(e) => setDonationAlumniId(e.target.value)}>
                <option value="">Select Alumnus</option>
                {records.map((r) => (
                  <option key={r.id} value={r.id}>{r.first_name} {r.last_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Amount (GHS)</label>
              <input type="number" min="0" step="0.01" value={donationAmount} onChange={(e) => setDonationAmount(e.target.value)} style={{ width: 120 }} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Date</label>
              <input type="date" value={donationDate} onChange={(e) => setDonationDate(e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 160 }}>
              <label>Campaign (optional)</label>
              <input type="text" value={donationCampaign} onChange={(e) => setDonationCampaign(e.target.value)} placeholder="e.g., 10th Anniversary Reunion" />
            </div>
            <button className="btn btn-primary" onClick={handleLogDonation} disabled={loggingDonation}>
              <HandCoins size={18} />
              {loggingDonation ? 'Logging...' : 'Log Donation'}
            </button>
          </div>

          {donations.length > 0 && (
            <ul style={{ marginTop: 16, paddingLeft: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
              {donations.map((d) => (
                <li key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f7f7f7', borderRadius: 6, fontSize: 13 }}>
                  <span style={{ flex: 1 }}>
                    GHS {Number(d.amount).toFixed(2)} from {getAlumniName(d.alumni_id)} on {d.donation_date}
                    {d.campaign ? ` — ${d.campaign}` : ''}
                  </span>
                  {d.approval_status === 'pending' && <span className="badge inactive" style={{ fontSize: 11 }}>Pending Approval</span>}
                  {d.approval_status === 'rejected' && (
                    <span className="badge" style={{ fontSize: 11, background: '#fee2e2', color: '#dc2626' }} title={d.rejection_reason || ''}>Rejected</span>
                  )}
                  {d.approval_status === 'pending' && d.created_by !== user?.id && (
                    <>
                      <button className="btn-icon" title="Approve" onClick={() => handleApproveDonation(d)}>
                        <Check size={16} color="#16a34a" />
                      </button>
                      <button className="btn-icon btn-danger" title="Reject" onClick={() => handleRejectDonation(d)}>
                        <XIcon size={16} />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} className="filter-select">
            <option value="all">All Years</option>
            {graduationYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div className="table-container">
          {filteredRecords.length === 0 ? (
            <div className="empty-state">
              <p>No alumni records found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Graduation Year</th>
                  <th>Occupation</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Opt-In</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => (
                  <tr key={record.id}>
                    <td>{record.first_name} {record.last_name}</td>
                    <td>{record.graduation_year}</td>
                    <td>{record.current_occupation || '-'}</td>
                    <td>{record.phone || '-'}</td>
                    <td>{record.email || '-'}</td>
                    <td>{record.opt_in_communications ? 'Yes' : 'No'}</td>
                    <td className="action-buttons">
                      <button className="btn-icon" title="Edit" onClick={() => openEditModal(record)}>
                        <PencilSimple size={18} />
                      </button>
                      <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteTarget(record.id)}>
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingId ? 'Edit Alumni Record' : 'Add Alumni Record'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input type="text" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input type="text" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Graduation Year *</label>
                  <input type="text" value={formData.graduation_year} onChange={(e) => setFormData({ ...formData, graduation_year: e.target.value })} placeholder="e.g., 2018" required />
                </div>
                <div className="form-group">
                  <label>Last Class Completed</label>
                  <input type="text" value={formData.last_class_completed} onChange={(e) => setFormData({ ...formData, last_class_completed: e.target.value })} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Current Occupation</label>
                  <input type="text" value={formData.current_occupation} onChange={(e) => setFormData({ ...formData, current_occupation: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Current Institution</label>
                  <input type="text" value={formData.current_institution} onChange={(e) => setFormData({ ...formData, current_institution: e.target.value })} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </div>
              </div>

              <div className="form-group">
                <label>Address</label>
                <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.opt_in_communications}
                    onChange={(e) => setFormData({ ...formData, opt_in_communications: e.target.checked })}
                    style={{ width: 'auto', marginRight: 8 }}
                  />
                  Opted in to communications
                </label>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update' : 'Add'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Alumni Record"
        message="This will permanently delete this alumni record. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />

      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="info"
        input={{ placeholder: 'Reason (optional)', defaultValue: promptDialog?.defaultValue || '' }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default AlumniRecordsPage;
